import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { AdminService, AdminStats } from '../../services/admin.service';
import { AuthService } from '../../services/auth.service';

@Component({
  selector: 'app-admin-dashboard',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './admin-dashboard.component.html',
  styleUrl: './admin-dashboard.component.css',
})
export class AdminDashboardComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  stats: AdminStats | null = null;
  loading = true;
  error: string | null = null;
  currentAdmin: any = null;

  quickActions = [
    {
      title: 'Pending Listings',
      description: 'Review and approve pending listings',
      icon: '📋',
      route: '/admin/pending-listings',
      color: '#3b82f6',
    },
    {
      title: 'Admin Actions',
      description: 'View admin activity log',
      icon: '📊',
      route: '/admin/actions',
      color: '#6b7280',
    },
    {
      title: 'Marketing',
      description: 'Send marketing campaigns to subscribers',
      icon: '📧',
      route: '/admin/marketing',
      color: '#10b981',
    },
    {
      title: 'Support',
      description: 'Check support tickets from users',
      icon: '⚙️',
      route: '/admin/support',
      color: '#10b981',
    },
    {
      title: 'Embeddings',
      description: 'Manage AI search embeddings for listings',
      icon: '🤖',
      route: '/admin/embeddings',
      color: '#8b5cf6',
    },
  ];

  constructor(
    private adminService: AdminService,
    private authService: AuthService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.loadCurrentAdmin();
    this.loadDashboardStats();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadCurrentAdmin(): void {
    this.authService.currentUser$
      .pipe(takeUntil(this.destroy$))
      .subscribe((user) => {
        this.currentAdmin = user;
        if (!user?.is_admin) {
          this.router.navigate(['/home']);
        }
      });
  }

  private loadDashboardStats(): void {
    this.loading = true;
    this.error = null;

    this.adminService
      .getDashboardStats()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (stats) => {
          this.stats = stats;
          this.loading = false;
        },
        error: (error) => {
          console.error('Error loading dashboard stats:', error);
          this.error = error.message || 'Failed to load dashboard statistics';
          this.loading = false;
        },
      });
  }

  onRefresh(): void {
    this.loadDashboardStats();
  }

  navigateTo(route: string): void {
    this.router.navigate([route]);
  }

  onLogout(): void {
    if (confirm('Are you sure you want to log out?')) {
      this.authService.logout().subscribe({
        next: (response) => {
          this.router.navigate(['/login']);
        },
      });
    }
  }

  getStatValue(statKey: keyof AdminStats): number {
    return this.stats?.[statKey] ?? 0;
  }

  getGreeting(): string {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  }

  getAdminName(): string {
    if (this.currentAdmin) {
      return `${this.currentAdmin.first_name} ${this.currentAdmin.last_name}`.trim();
    }
    return 'Admin';
  }

  getUrgentItems(): { title: string; count: number; route: string }[] {
    if (!this.stats) return [];

    const urgentItems = [];

    if (this.stats.pending_listings > 0) {
      urgentItems.push({
        title: 'Pending Listings',
        count: this.stats.pending_listings,
        route: '/admin/pending-listings',
      });
    }

    return urgentItems;
  }

  hasUrgentItems(): boolean {
    return this.getUrgentItems().length > 0;
  }
}
